/**
 * Настройки → Пороги (docs/06-notifications.md).
 *
 * Пороги общие для всех сервисов и переопределяемые для каждого отдельно.
 * Месячных бюджетов и лимитов здесь нет: потолок расходов не задаётся,
 * приложение только предупреждает.
 */
import { useEffect, useState } from 'react';
import { Badge, Button, Card, Icon, Input, Select } from '@/ds';
import { Provenance } from '@/components/money';
import {
  useNotificationSettings,
  useOperation,
  useServices,
  type ThresholdSetting,
} from '@/api/hooks';
import { formatMoney, plural } from '@/lib/format';
import { minorToInput, parseMinor } from '@/lib/money';
import { useControlSize } from '@/lib/useControlSize';
import { QueryState } from '@/components/QueryState';

/** Единица склоняется по числу: 1 день, 2 дня, 5 дней. */
function unitWord(unit: ThresholdSetting['unit'], value: number): string {
  switch (unit) {
    case 'days':
      return plural(value, 'день', 'дня', 'дней');
    case 'percent':
      return plural(value, 'процент', 'процента', 'процентов');
    case 'times':
      return plural(value, 'раз', 'раза', 'раз');
    default:
      return '';
  }
}

/** Деньги вводятся как на всех остальных экранах, остальное целым числом. */
function toInputValue(setting: ThresholdSetting): string {
  if (setting.unit === 'money') return minorToInput(setting.value);
  return String(setting.value);
}

function ThresholdField({
  setting,
  currency,
  onSave,
  onReset,
  busy,
}: {
  setting: ThresholdSetting;
  currency: string;
  onSave: (value: number) => void;
  onReset?: (() => void) | undefined;
  busy: boolean;
}) {
  const size = useControlSize();
  const [value, setValue] = useState(toInputValue(setting));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(toInputValue(setting));
  }, [setting.value, setting.serviceId]);

  const dirty = value !== toInputValue(setting);

  const save = () => {
    setError(null);
    if (setting.unit === 'money') {
      const parsed = parseMinor(value);
      if (parsed === null) {
        setError('Введите сумму, например 20 или 20,50');
        return;
      }
      onSave(parsed);
      return;
    }

    const number = Number(value.replace(',', '.'));
    if (!Number.isFinite(number) || number < 0) {
      setError('Введите целое число');
      return;
    }
    onSave(Math.round(number));
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-4)',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        paddingBottom: 'var(--space-4)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <Input
          label={setting.title}
          size={size}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          {...(error ? { error } : { help: setting.hint })}
        />
      </div>

      <div style={{ flex: '0 0 auto', paddingBottom: 6 }}>
        <span style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
          {setting.unit === 'money' ? currency : unitWord(setting.unit, Number(value.replace(',', '.')) || 0)}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', paddingBottom: 2 }}>
        <Button variant="secondary" size="sm" disabled={busy || !dirty} onClick={save}>
          Сохранить
        </Button>
        {onReset ? (
          <Button variant="ghost" size="sm" disabled={busy} onClick={onReset}>
            К общему
          </Button>
        ) : null}
      </div>

      <div style={{ flex: '1 1 100%' }}>
        <Provenance>
          {setting.unit === 'money'
            ? `По умолчанию ${formatMoney(setting.defaultValue, currency)}`
            : `По умолчанию ${setting.defaultValue} ${unitWord(setting.unit, setting.defaultValue)}`}
        </Provenance>
      </div>
    </div>
  );
}

export function ThresholdsTab() {
  const size = useControlSize();
  const settings = useNotificationSettings(true);
  const services = useServices();
  const [adding, setAdding] = useState('');

  const setThreshold = useOperation<
    { kind: ThresholdSetting['key']; serviceId?: string | null; value: number | null },
    { kind: string }
  >('notification.set_threshold');

  if (settings.isLoading || settings.isError) {
    return <QueryState query={settings} what="пороги" />;
  }

  const data = settings.data;
  if (!data) return null;

  const overridden = new Set(data.serviceOverrides.map((item) => item.serviceId));
  const candidates = (services.data?.services ?? []).filter(
    (service) => service.isActive && !overridden.has(service.id),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <p style={{ font: '400 13px/1.5 var(--font-text)', color: 'var(--text-muted)', maxWidth: 620 }}>
        Порог — условие, при котором приходит предупреждение. Для одного сервиса может сработать
        несколько порогов сразу: уведомление будет одно, более строгое по уровню, с обоими фактами
        в тексте. Потолок расходов приложение не задаёт и трату не блокирует.
      </p>

      <Card padding="md">
        <div
          style={{
            font: '500 16px var(--font-display)',
            letterSpacing: 'var(--ls-heading)',
            color: 'var(--text-strong)',
            marginBottom: 'var(--space-5)',
          }}
        >
          Общие пороги
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {data.thresholds.map((setting) => (
            <ThresholdField
              key={setting.key}
              setting={setting}
              currency="USD"
              busy={setThreshold.isPending}
              onSave={(value) => setThreshold.mutate({ kind: setting.key, serviceId: null, value })}
            />
          ))}
        </div>
      </Card>

      {data.serviceOverrides.map((override) => (
        <Card padding="md" key={override.serviceId}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-5)',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                font: '500 16px var(--font-display)',
                letterSpacing: 'var(--ls-heading)',
                color: 'var(--text-strong)',
              }}
            >
              {override.serviceName}
            </span>
            <Badge tone="brand">свои пороги</Badge>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {override.thresholds.map((setting) => (
              <ThresholdField
                key={setting.key}
                setting={setting}
                currency={override.currency}
                busy={setThreshold.isPending}
                onSave={(value) =>
                  setThreshold.mutate({ kind: setting.key, serviceId: override.serviceId, value })
                }
                onReset={() =>
                  setThreshold.mutate({
                    kind: setting.key,
                    serviceId: override.serviceId,
                    value: null,
                  })
                }
              />
            ))}
          </div>
        </Card>
      ))}

      <Card padding="md" tone="sunken">
        <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px' }}>
            <Select
              label="Свои пороги для сервиса"
              size={size}
              options={[
                { value: '', label: 'Выберите сервис' },
                ...candidates.map((service) => ({ value: service.id, label: service.name })),
              ]}
              value={adding}
              onChange={(e) => setAdding(e.target.value)}
              help="Незаполненные пороги останутся общими"
            />
          </div>
          <Button
            variant="secondary"
            size={size}
            disabled={!adding || setThreshold.isPending}
            iconLeft={<Icon name="Plus" size={15} />}
            onClick={() => {
              const service = candidates.find((item) => item.id === adding);
              if (!service) return;
              // Переопределение начинается с копии общего значения.
              const base = data.thresholds.find((item) => item.key === 'absolute');
              setThreshold.mutate({
                kind: 'absolute',
                serviceId: service.id,
                value: base?.value ?? 2000,
              });
              setAdding('');
            }}
          >
            Добавить
          </Button>
        </div>
      </Card>
    </div>
  );
}
